import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Edit2, Trash2, X } from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import LoadingButton from '../components/LoadingButton'
import ConfirmDialog from '../components/ConfirmDialog'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import { useModalScrollLock } from '../hooks/useModalScrollLock'
import { SkeletonTable } from '../components/SkeletonLoader'
import { extractErrorMessage } from '../utils/errorHandler'

interface Coupon {
  id: string
  code: string
  description?: string
  discountType: 'PERCENTAGE' | 'FIXED'
  discountValue: number
  minAmount?: number
  maxDiscount?: number
  usageLimit?: number
  usageCount: number
  validFrom: string
  validUntil: string
}

// Zod Validation Schema for Coupon Form
const couponSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(3, 'Coupon code must be at least 3 characters.')
      .max(20, 'Coupon code cannot exceed 20 characters.')
      .regex(/^[A-Z0-9_-]+$/, 'Code can only contain uppercase letters, numbers, hyphens, and underscores.'),
    description: z
      .string()
      .trim()
      .max(250, 'Description cannot exceed 250 characters.')
      .optional()
      .or(z.literal('')),
    discountType: z.enum(['PERCENTAGE', 'FIXED']),
    discountValue: z
      .string()
      .trim()
      .min(1, 'Discount value is required.')
      .refine(
        (val) => {
          const num = parseFloat(val)
          return !isNaN(num) && num > 0
        },
        'Discount value must be greater than 0.'
      ),
    minAmount: z
      .string()
      .trim()
      .optional()
      .or(z.literal(''))
      .refine(
        (val) => {
          if (!val) return true
          const num = parseFloat(val)
          return !isNaN(num) && num >= 0
        },
        'Minimum amount cannot be negative.'
      ),
    maxDiscount: z
      .string()
      .trim()
      .optional()
      .or(z.literal(''))
      .refine(
        (val) => {
          if (!val) return true
          const num = parseFloat(val)
          return !isNaN(num) && num >= 0
        },
        'Maximum discount cannot be negative.'
      ),
    usageLimit: z
      .string()
      .trim()
      .optional()
      .or(z.literal(''))
      .refine(
        (val) => {
          if (!val) return true
          const num = parseFloat(val)
          return !isNaN(num) && Number.isInteger(num) && num >= 1
        },
        'Usage limit must be a whole number of at least 1.'
      ),
    validFrom: z
      .string()
      .trim()
      .min(1, 'Valid From date is required.'),
    validUntil: z
      .string()
      .trim()
      .min(1, 'Valid Until date is required.'),
  })
  .superRefine((data, ctx) => {
    // 1. Percentage discount check (<= 100%)
    const dVal = parseFloat(data.discountValue)
    if (data.discountType === 'PERCENTAGE' && !isNaN(dVal) && dVal > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Percentage discount cannot exceed 100%.',
        path: ['discountValue'],
      })
    }

    // 2. Maximum Discount vs Minimum Order Amount validation (Max Discount <= Min Order Amount for PERCENTAGE coupons)
    if (data.discountType === 'PERCENTAGE' && data.minAmount && data.maxDiscount) {
      const minAmt = parseFloat(data.minAmount)
      const maxDisc = parseFloat(data.maxDiscount)
      if (!isNaN(minAmt) && !isNaN(maxDisc) && maxDisc > minAmt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Maximum Discount cannot be greater than the Minimum Order Amount.',
          path: ['maxDiscount'],
        })
      }
    }

    // 3. Past date checks & Date range validation (same-day start & end allowed)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (data.validFrom) {
      const fromDate = new Date(data.validFrom)
      if (!isNaN(fromDate.getTime()) && fromDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Valid From date cannot be in the past.',
          path: ['validFrom'],
        })
      }
    }

    if (data.validUntil) {
      const untilDate = new Date(data.validUntil)
      if (!isNaN(untilDate.getTime()) && untilDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Valid Until date cannot be in the past.',
          path: ['validUntil'],
        })
      }
    }

    if (data.validFrom && data.validUntil) {
      const fromDate = new Date(data.validFrom)
      const untilDate = new Date(data.validUntil)
      if (!isNaN(fromDate.getTime()) && !isNaN(untilDate.getTime())) {
        if (untilDate < fromDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Valid Until date must be on or after Valid From date.',
            path: ['validUntil'],
          })
        }
      }
    }
  })

type CouponFormData = z.infer<typeof couponSchema>

export default function CouponsPage() {
  const filterManager = useAdminFilters({ defaultLimit: 10 })
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null)
  const [total, setTotal] = useState(0)
  // Prevent background page scroll when modal is open
  useModalScrollLock(showModal)
  const [submitButtonState, setSubmitButtonState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; couponId: string | null; isLoading: boolean }>({
    isOpen: false,
    couponId: null,
    isLoading: false,
  })
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      code: '',
      description: '',
      discountType: 'PERCENTAGE',
      discountValue: '' as any,
      minAmount: '' as any,
      maxDiscount: '' as any,
      usageLimit: '' as any,
      validFrom: '',
      validUntil: '',
    },
  })

  const validFromValue = watch('validFrom')
  const validUntilValue = watch('validUntil')
  const discountTypeValue = watch('discountType')
  const minAmountValue = watch('minAmount')
  const maxDiscountValue = watch('maxDiscount')

  // Clear maxDiscount when switching discountType to FIXED
  useEffect(() => {
    if (discountTypeValue === 'FIXED') {
      setValue('maxDiscount', '' as any)
    }
  }, [discountTypeValue, setValue])

  // Automatic revalidation when minAmount or maxDiscount changes
  useEffect(() => {
    if (maxDiscountValue) {
      trigger('maxDiscount')
    }
  }, [minAmountValue, maxDiscountValue, trigger])

  // Automatic revalidation when validFrom or validUntil changes
  useEffect(() => {
    if (validUntilValue) {
      trigger('validUntil')
    }
  }, [validFromValue, validUntilValue, trigger])

  useEffect(() => {
    fetchCoupons()
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)])

  const fetchCoupons = async () => {
    try {
      setLoading(true)
      const response = await api.getCoupons(page, limit, debouncedSearch, filterManager.filters)
      setCoupons(response.data.data || [])
      setTotal(response.data.pagination?.total || 0)
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage)
      console.error('Fetch coupons error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddClick = () => {
    setEditingId(null)
    const today = new Date().toISOString().split('T')[0]
    reset({
      code: '',
      description: '',
      discountType: 'PERCENTAGE',
      discountValue: '' as any,
      minAmount: '' as any,
      maxDiscount: '' as any,
      usageLimit: '' as any,
      validFrom: today,
      validUntil: '',
    })
    setShowModal(true)
  }

  const handleEditClick = (coupon: Coupon) => {
    setEditingId(coupon.id)
    setSelectedCoupon(coupon)
    reset({
      code: coupon.code,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: String(coupon.discountValue),
      minAmount: coupon.minAmount !== undefined && coupon.minAmount !== null ? String(coupon.minAmount) : '',
      maxDiscount: coupon.maxDiscount !== undefined && coupon.maxDiscount !== null ? String(coupon.maxDiscount) : '',
      usageLimit: coupon.usageLimit !== undefined && coupon.usageLimit !== null ? String(coupon.usageLimit) : '',
      validFrom: coupon.validFrom ? coupon.validFrom.split('T')[0] : '',
      validUntil: coupon.validUntil ? coupon.validUntil.split('T')[0] : '',
    })
    setShowModal(true)
  }

  const onFormSubmit = async (data: CouponFormData) => {
    setSubmitButtonState('loading')

    try {
      const payload = {
        code: data.code.toUpperCase(),
        description: data.description || undefined,
        discountType: data.discountType,
        discountValue: Number(data.discountValue),
        minAmount: data.minAmount !== undefined && data.minAmount !== null && data.minAmount !== ('' as any) ? Number(data.minAmount) : undefined,
        maxDiscount: data.discountType === 'PERCENTAGE' && data.maxDiscount !== undefined && data.maxDiscount !== null && data.maxDiscount !== ('' as any) ? Number(data.maxDiscount) : undefined,
        usageLimit: data.usageLimit !== undefined && data.usageLimit !== null && data.usageLimit !== ('' as any) ? Number(data.usageLimit) : undefined,
        validFrom: new Date(data.validFrom),
        validUntil: new Date(data.validUntil),
      }

      if (editingId) {
        await api.updateCoupon(editingId, payload)
        toast.success('Coupon updated successfully')
      } else {
        await api.createCoupon(payload)
        toast.success('Coupon created successfully')
      }

      setSubmitButtonState('success')
      setTimeout(() => {
        setShowModal(false)
        setSubmitButtonState('idle')
        fetchCoupons()
      }, 500)
    } catch (error: any) {
      setSubmitButtonState('error')
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage)
      setTimeout(() => setSubmitButtonState('idle'), 2000)
    }
  }

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ isOpen: true, couponId: id, isLoading: false })
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.couponId) return

    setDeleteConfirm((prev) => ({ ...prev, isLoading: true }))

    try {
      await api.deleteCoupon(deleteConfirm.couponId)
      toast.success('Coupon deleted successfully')
      setDeleteConfirm({ isOpen: false, couponId: null, isLoading: false })
      fetchCoupons()
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage)
      setDeleteConfirm((prev) => ({ ...prev, isLoading: false }))
    }
  }

  const handleCancelDelete = () => {
    setDeleteConfirm({ isOpen: false, couponId: null, isLoading: false })
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const checkIsExpired = (validUntilStr?: string) => {
    if (!validUntilStr) return false
    const endDate = new Date(validUntilStr)
    if (isNaN(endDate.getTime())) return false
    // Use end-of-day cutoff for that calendar day (23:59:59.999)
    const year = endDate.getFullYear()
    const month = endDate.getMonth()
    const day = endDate.getDate()
    const endOfDay = new Date(year, month, day, 23, 59, 59, 999)
    const now = new Date()
    return now > endOfDay
  }

  const pages = Math.ceil(total / limit)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">Coupons</h1>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] transition font-semibold text-sm shadow-sm"
        >
          <Plus size={18} />
          Add Coupon
        </button>
      </div>

      <AdminFilters
        search={search}
        searchPlaceholder="Search coupons by code..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={loading}
      />

      {loading ? (
        <SkeletonTable rows={5} cols={8} />
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-[#F6EEE7]">
          <p className="text-gray-600">
            {hasActiveFilters ? 'No coupons match your search filters.' : 'No coupons found. Create one to get started!'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#F6EEE7] shadow overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#F6EEE7]/80 border-b-2 border-[#F6EEE7]">
              <tr>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Code
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Min Order
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Max Cap
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Validity Period
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-5 py-3.5 text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-5 py-3.5 text-right text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F6EEE7]">
              {coupons.map((coupon) => {
                const expired = checkIsExpired(coupon.validUntil)

                return (
                  <tr key={coupon.id} className="hover:bg-[#F6EEE7]/40 transition-colors">
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className="font-bold text-gray-900 bg-[#F6EEE7]/80 px-2.5 py-1 rounded-md text-xs font-mono tracking-wider border border-[#E8DCD1]">
                        {coupon.code}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {coupon.discountType === 'PERCENTAGE' ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                          Percentage
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Amount
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap font-bold text-[#6D3A0D] text-sm">
                      {coupon.discountType === 'PERCENTAGE'
                        ? `${coupon.discountValue}%`
                        : `₹${coupon.discountValue}`}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">
                      {coupon.minAmount !== undefined && coupon.minAmount !== null ? `₹${coupon.minAmount}` : '—'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">
                      {coupon.maxDiscount !== undefined && coupon.maxDiscount !== null ? `₹${coupon.maxDiscount}` : '—'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-700">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-gray-800">
                          <span className="text-gray-400 font-normal">From:</span> {formatDate(coupon.validFrom)}
                        </span>
                        <span className={`font-medium ${expired ? 'text-red-600' : 'text-gray-800'}`}>
                          <span className="text-gray-400 font-normal">Until:</span> {formatDate(coupon.validUntil)}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {expired ? (
                        <span className="px-2.5 py-0.5 text-xs font-bold bg-red-100 text-red-700 border border-red-200 rounded-full inline-flex items-center gap-1.5 shadow-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                          Expired
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full inline-flex items-center gap-1.5 shadow-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">
                      {coupon.usageCount} / {coupon.usageLimit || '∞'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-right text-xs">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditClick(coupon)}
                          title="Edit Coupon"
                          className="p-1.5 text-[#6D3A0D] hover:bg-[#F6EEE7] rounded-lg transition-colors font-semibold"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(coupon.id)}
                          title="Delete Coupon"
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-semibold"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="mt-6">
          <AdminPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={loading}
            showInfo={true}
          />
        </div>
      )}

      {/* Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#6D3A0D]">
                {editingId ? 'Edit Coupon' : 'Add New Coupon'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onFormSubmit)} noValidate className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('code')}
                    type="text"
                    onChange={(e) => setValue('code', e.target.value.toUpperCase(), { shouldValidate: true })}
                    placeholder="WELCOME10"
                    maxLength={20}
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.code ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  />
                  {errors.code && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.code.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('discountType')}
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.discountType ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  >
                    <option value="PERCENTAGE">Percentage (%)</option>
                    <option value="FIXED">Fixed Amount (₹)</option>
                  </select>
                  {errors.discountType && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.discountType.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
                    Discount Value {discountTypeValue === 'PERCENTAGE' ? '(%)' : '(₹)'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('discountValue')}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={discountTypeValue === 'PERCENTAGE' ? '10' : '100'}
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.discountValue ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  />
                  {errors.discountValue && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.discountValue.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">Min Order Amount (₹)</label>
                  <input
                    {...register('minAmount')}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="500"
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.minAmount ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  />
                  {errors.minAmount && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.minAmount.message}</p>
                  )}
                </div>

                {discountTypeValue === 'PERCENTAGE' && (
                  <div>
                    <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">Max Discount (₹)</label>
                    <input
                      {...register('maxDiscount')}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="100"
                      disabled={isSubmitting}
                      className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                        errors.maxDiscount ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                      }`}
                    />
                    {errors.maxDiscount && (
                      <p className="text-red-500 text-xs mt-1 font-medium">{errors.maxDiscount.message}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">Usage Limit</label>
                  <input
                    {...register('usageLimit')}
                    type="number"
                    min="1"
                    placeholder="100"
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.usageLimit ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  />
                  {errors.usageLimit && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.usageLimit.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('validFrom')}
                    type="date"
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.validFrom ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  />
                  {errors.validFrom && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.validFrom.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
                    Valid Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('validUntil')}
                    type="date"
                    min={validFromValue || undefined}
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                      errors.validUntil ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                    }`}
                  />
                  {errors.validUntil && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.validUntil.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">Description</label>
                <textarea
                  {...register('description')}
                  rows={3}
                  disabled={isSubmitting}
                  placeholder="Optional details or terms about this coupon..."
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none disabled:opacity-50 ${
                    errors.description ? 'border-red-500 focus:border-red-500 bg-red-50/20' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                  }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.description.message}</p>
                )}
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={isSubmitting || submitButtonState === 'loading'}
                  className="flex-1 px-4 py-2 border-2 border-[#F6EEE7] text-[#6D3A0D] rounded-lg font-semibold hover:bg-[#F6EEE7] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <LoadingButton
                  type="submit"
                  state={submitButtonState}
                  disabled={isSubmitting}
                  variant="primary"
                  size="md"
                  className="flex-1 bg-[#6D3A0D] hover:bg-[#5D2A0D]"
                  loadingText="Saving..."
                  successText="Saved!"
                  errorText="Failed"
                >
                  {editingId ? 'Update' : 'Create'}
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Coupon"
        message="Are you sure you want to delete this coupon? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={deleteConfirm.isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  )
}
